import React from "react";
import { useTranslation } from "react-i18next";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  error: {
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  trace: {
    fontFamily: "monospace",
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },
  usageSection: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  usageHeading: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  usageList: {
    margin: 0,
    paddingLeft: theme.spacing(3),
  },
}));

export interface ProductUsage {
  gradingOrderItemCount?: number;
  gradingOrderExamples?: string[];
  cartItemCount?: number;
  cartExamples?: string[];
}

interface ErrorDialogProps {
  open: boolean;
  onClose: () => void;
  error: {
    detailedMessage: string;
    traceId?: string | null;
    usage?: ProductUsage | null;
  } | null;
}

export const ErrorDialog: React.FC<ErrorDialogProps> = ({
  open,
  onClose,
  error,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('ui');

  if (!error) return null;

  const usage = error.usage;
  const hasUsage =
    !!usage &&
    (((usage.gradingOrderItemCount ?? 0) > 0) || ((usage.cartItemCount ?? 0) > 0));

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t('errorDialog.title')}</DialogTitle>
      <DialogContent>
        <Typography className={classes.error}>
          {error.detailedMessage}
        </Typography>
        {hasUsage && (
          <div className={classes.usageSection}>
            {(usage!.gradingOrderItemCount ?? 0) > 0 && (
              <div>
                <Typography className={classes.usageHeading}>
                  {t('errorDialog.usedInGradingOrders', { count: usage!.gradingOrderItemCount })}
                </Typography>
                {!!usage!.gradingOrderExamples?.length && (
                  <ul className={classes.usageList}>
                    {usage!.gradingOrderExamples!.map((ex, i) => (
                      <li key={`order-${i}`}>
                        <Typography variant="body2">{ex}</Typography>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {(usage!.cartItemCount ?? 0) > 0 && (
              <div>
                <Typography className={classes.usageHeading}>
                  {t('errorDialog.usedInCarts', { count: usage!.cartItemCount })}
                </Typography>
                {!!usage!.cartExamples?.length && (
                  <ul className={classes.usageList}>
                    {usage!.cartExamples!.map((ex, i) => (
                      <li key={`cart-${i}`}>
                        <Typography variant="body2">{ex}</Typography>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
        {error.traceId && (
          <Typography className={classes.trace}>
            {t('errorDialog.traceId', { id: error.traceId })}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t('common:actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
